import { useEffect } from 'react';
import { useChatContext } from 'stream-chat-react';
import { useUpsertConsumerByIdMutation } from 'generated/graphql';
import toast from 'react-hot-toast';
import { useMemo } from 'react';
import {
  ChannelFilters,
  ChannelOptions,
  ChannelSort,
  ChannelSortBase,
  DefaultGenerics,
} from 'stream-chat';
import { add } from 'date-fns';

export type ChatViewModel = {
  channelListOptions: {
    filters: ChannelFilters;
    options: ChannelOptions;
    sort: ChannelSortBase<DefaultGenerics>;
  };
};

export const useChatViewModel = ({
  userId,
}: {
  userId: string;
}): ChatViewModel => {
  const url = new URL(window.location.href);
  const consumerId = url.searchParams.get('userId');
  const { client, setActiveChannel } = useChatContext();
  const [upsertConsumerById] = useUpsertConsumerByIdMutation({
    variables: {
      id: consumerId ? Number(consumerId) : 0,
    },
  });

  useEffect(() => {
    if (client && consumerId) {
      const createOneChannel = async () => {
        try {
          await upsertConsumerById();

          const conversation = client.channel('messaging', null, {
            members: [`${userId}`, `${consumerId}`],
          });

          await conversation.create();
          setActiveChannel(conversation, { limit: 0, offset: 1 });
          await conversation.watch();
        } catch (error) {
          toast.error(
            `Could not start conversation this user id: ${consumerId}`,
          );
          console.warn(error);
        }
      };
      createOneChannel();
    }
  }, [client]);

  const channelListOptions = useMemo(
    () => getChannelListOptions(userId),
    [userId],
  );

  return {
    channelListOptions,
  };
};

export const getChannelListOptions = (user: string | null) => {
  const dateInTheFuture = add(new Date(), { days: 1 });
  const filters: ChannelFilters = {
    type: 'messaging',
    members: { $in: [user] },
    last_message_at: { $lt: dateInTheFuture.toISOString() },
  };

  const options: ChannelOptions = {
    state: true,
    watch: true,
    presence: true,
    limit: 100,
  };

  const sort: ChannelSort = {
    last_message_at: -1,
  };

  return {
    filters,
    options,
    sort,
  };
};
